import React, { Component } from 'react'
import PropTypes from 'prop-types'

import cx from 'classnames'
import memoize from 'memoize-one'

import analyticsUtils from '../../helpers/analytics-utils'
import style from './more-text.module.scss'

const LARGE_HEIGHT = '3000px'
const TEXT_STYLES = '_plain'
// Could use Node.TEXT_NODE, but this value does not work in IE11 and edge
const TEXT_NODE = 3
const HEADER_HEIGHT = 88
// Need to wait for the transition to complete before determining whether a show
// more button is required
const TRANSITION_TIME = 550

const isHtml = memoize(text => /<[a-z][\s\S]*>/i.test(text))
const wrapNodes = memoize(text => {
  if (typeof document === 'undefined') {
    return text
  }

  const createTextElement = t => {
    const elm = document.createElement('p')
    elm.innerHTML = t
    return elm
  }
  const div = document.createElement('div')
  div.innerHTML = text
  const children = div.childNodes
  const parsedChildren = document.createElement('div')
  Array.from(children).forEach(node => {
    if (node.nodeType === TEXT_NODE) {
      const textContent = node.textContent.trim()
      if (textContent.length > 0) {
        parsedChildren.appendChild(createTextElement(textContent))
      }
    } else {
      parsedChildren.appendChild(node)
    }
  })
  return parsedChildren.innerHTML
})

function getTotalSize(elm, ...styleAttributes) {
  return styleAttributes.reduce((total, attribute) => {
    const updated = total + parseInt(window.getComputedStyle(elm)[attribute])
    return updated
  }, 0)
}

// @ref: https://stackoverflow.com/questions/123999/how-to-tell-if-a-dom-element-is-visible-in-the-current-viewport/7557433#7557433
function isElementInViewport(el) {
  const rect = el.getBoundingClientRect()

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export default class MoreText extends Component {
  static propTypes = {
    text: PropTypes.string,
    limit: PropTypes.number,
    classNames: PropTypes.object,
  }

  static defaultProps = {
    classNames: {},
  }

  constructor(props) {
    super(props)
    this.textBlock = React.createRef()

    const initialStyle = {
      maxHeight: 0,
    }

    if (!isHtml(this.props.text)) {
      Object.assign(initialStyle, {
        WebkitLineClamp: this.props.limit,
        WebkitBoxOrient: 'vertical',
      })
    }

    this.state = {
      expanded: false,
      showMore: false,
      style: initialStyle,
    }
  }

  componentDidMount() {
    this.applyHeight()
  }

  componentDidUpdate(prevProps) {
    if (this.props.text !== prevProps.text) {
      this.applyHeight()
    }
  }

  showMore = () => {
    setTimeout(() => {
      if (
        this.textBlock.current !== undefined &&
        this.textBlock.current !== null &&
        this.textBlock.current.clientHeight <
          this.textBlock.current.scrollHeight
      ) {
        this.setState({ showMore: true })
      } else {
        this.setState({ showMore: false })
      }
    }, TRANSITION_TIME)
  }

  // @ref: https://stackoverflow.com/questions/4392868/javascript-find-divs-line-height-not-css-property-but-actual-line-height
  getLineHeight = element => {
    let temp = document.createElement(element.nodeName)
    if (element.style !== undefined) {
      temp.setAttribute(
        'style',
        `margin:0px;padding:0px;font-family:${element.style.fontFamily};font-size:${element.style.fontSize}`
      )
    }
    temp.innerHTML = 'test'
    if (element.parentNode !== undefined) {
      temp = element.parentNode.appendChild(temp)
    }
    const ret = temp.clientHeight
    if (temp.parentNode !== null) {
      temp.parentNode.removeChild(temp)
    }
    return ret
  }

  applyHeight = () => {
    const newStyle = Object.assign({}, this.state.style)

    let maxCollapsedHeight = 0

    if (isHtml(this.props.text)) {
      const nodes = this.textBlock.current.children
      if (nodes !== undefined) {
        if (nodes.length === 1) {
          const lineHeight = this.getLineHeight(nodes[0])
          maxCollapsedHeight = lineHeight * this.props.limit
          maxCollapsedHeight += getTotalSize(
            nodes[0],
            'margin-top',
            'padding-top',
            'border-top-width'
          )
        } else {
          const maxItems = Math.min(nodes.length, this.props.limit)

          for (let index = 0; index < maxItems; index++) {
            maxCollapsedHeight += getTotalSize(nodes[index], 'margin-top')

            const nodeName = nodes[index].nodeName.toUpperCase()
            if (nodeName === 'BR') {
              const val = getTotalSize(nodes[index], 'line-height')
              maxCollapsedHeight += val
            } else if (nodeName === 'HR') {
              maxCollapsedHeight += getTotalSize(
                nodes[index],
                'border-top-width',
                'border-bottom-width'
              )
            } else {
              // We need to set inline block on the following elements so that
              // we can get their height correctly
              if (
                [
                  'B',
                  'I',
                  'U',
                  'EM',
                  'STRONG',
                  'SUP',
                  'SUB',
                  'BIG',
                  'SMALL',
                  'LI',
                  'INS',
                  'DEL',
                ].findIndex(val => nodeName === val) >= 0
              ) {
                nodes[index].style.display = 'inline-block'
              }
              maxCollapsedHeight += nodes[index].clientHeight
            }
          }
        }
      }
    } else {
      const lineHeight = this.getLineHeight(this.textBlock.current)
      maxCollapsedHeight = this.props.limit * lineHeight
    }

    newStyle.maxHeight = `${maxCollapsedHeight}px`

    this.setState(
      { style: newStyle, maxCollapsedHeight, expanded: false },
      this.showMore
    )
  }

  sendEvent = () => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.PRODUCTS_SHOW_MORE_DETAIL,
      eventCategory: analyticsUtils.categories.MORE_LESS_CLICK,
      eventAction: this.state.expanded
        ? analyticsUtils.actions.LESS
        : analyticsUtils.actions.MORE,
    })
  }

  updateHeight = () => {
    const newStyle = Object.assign({}, this.state.style)

    newStyle.maxHeight = this.state.expanded
      ? `${this.state.maxCollapsedHeight}px`
      : LARGE_HEIGHT

    if (!isHtml(this.props.text)) {
      if (this.state.expanded) {
        newStyle.WebkitLineClamp = this.props.limit
      } else {
        delete newStyle.WebkitLineClamp
      }
    }
    return newStyle
  }

  handleClick = () => {
    this.sendEvent()
    const newStyle = this.updateHeight()
    this.setState(
      state => ({ expanded: !state.expanded, style: newStyle }),
      this.checkScroll
    )
  }

  checkScroll = () => {
    if (!this.state.expanded && !isElementInViewport(this.textBlock.current)) {
      window.scrollTo(
        0,
        this.textBlock.current.getBoundingClientRect()['top'] +
          window.scrollY -
          HEADER_HEIGHT
      )
    }
  }

  render() {
    return (
      <div className={cx(style['more-text'], this.props.classNames.wrapper)}>
        {isHtml(this.props.text) ? (
          <div
            ref={this.textBlock}
            style={this.state.style}
            className={style['more-text--text']}
            dangerouslySetInnerHTML={{
              __html: wrapNodes(this.props.text),
            }}
          />
        ) : (
          <div
            ref={this.textBlock}
            style={this.state.style}
            className={cx(style['more-text--text'], style[TEXT_STYLES])}
          >
            {this.props.text}
          </div>
        )}
        {this.state.showMore ? (
          <button
            className={cx(this.props.classNames.showMore)}
            onClick={this.handleClick}
          >
            SHOW {this.state.expanded ? 'LESS' : 'MORE'}
          </button>
        ) : null}
      </div>
    )
  }
}
